import { apiSlice } from "../../../features/api/apiSlice";

function pick(res: any) { return res?.data?.data ?? res?.data ?? res; }
function arr(res: any): any[] { const d = pick(res); return Array.isArray(d) ? d : []; }

export const cpdApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({

    // ── Admin: ledger entries ────────────────────────────────────────────────
    adminGetCpdEntries: b.query<any[], { user_id?: number; activity_type?: string; search?: string } | void>({
      query: (params) => ({ url: "/admin/cpd", params: params ?? {} }),
      transformResponse: (r) => arr(r),
      providesTags: [{ type: "AdminCpd" as const, id: "LIST" }],
    }),

    adminGetCpdSummary: b.query<any[], void>({
      query: () => "/admin/cpd/summary",
      transformResponse: (r) => arr(r),
      providesTags: [{ type: "CpdSummary" as const, id: "ALL" }],
    }),

    adminGetMemberCpd: b.query<any, number>({
      query: (userId) => `/admin/cpd/member/${userId}`,
      transformResponse: (r) => r,
    }),

    adminCreateCpdEntry: b.mutation<any, {
      user_id: number;
      points: number;
      activity_type: "manual" | "deduction";
      activity_date: string;
      notes?: string;
      event_id?: number;
    }>({
      query: (body) => ({ url: "/admin/cpd", method: "POST", body }),
      invalidatesTags: [
        { type: "AdminCpd" as const, id: "LIST" },
        { type: "CpdSummary" as const, id: "ALL" },
      ],
    }),

    // ── Admin: external submissions ──────────────────────────────────────────
    adminGetCpdSubmissions: b.query<any, { status?: string; search?: string; page?: number; per_page?: number } | void>({
      query: (params) => ({ url: "/admin/cpd/submissions", params: params ?? {} }),
      transformResponse: (r) => r,
      providesTags: [{ type: "AdminCpd" as const, id: "SUBMISSIONS" }],
    }),

    adminApproveCpdSubmission: b.mutation<any, { id: number; admin_notes?: string; event_id?: string }>({
      query: ({ id, ...body }) => ({ url: `/admin/cpd/submissions/${id}/approve`, method: "POST", body }),
      invalidatesTags: [
        { type: "AdminCpd" as const, id: "SUBMISSIONS" },
        { type: "AdminCpd" as const, id: "LIST" },
        { type: "CpdSummary" as const, id: "ALL" },
      ],
    }),

    adminRejectCpdSubmission: b.mutation<any, { id: number; admin_notes?: string }>({
      query: ({ id, ...body }) => ({ url: `/admin/cpd/submissions/${id}/reject`, method: "POST", body }),
      invalidatesTags: [{ type: "AdminCpd" as const, id: "SUBMISSIONS" }],
    }),

    // ── Member: my CPD ledger + total ───────────────────────────────────────
    getMyCpdLedger: b.query<{ data: any[]; total: number }, void>({
      query: () => "/my/cpd",
      transformResponse: (r: any) => ({ data: r?.data ?? [], total: r?.total ?? 0 }),
      providesTags: [{ type: "CpdLedger" as const, id: "MY" }],
    }),

    // ── Member: eligible CPD events for redemption ───────────────────────────
    getCpdEligibleEvents: b.query<any[], void>({
      query: () => "/my/cpd/eligible-events",
      transformResponse: (r) => arr(r),
      providesTags: [{ type: "CpdLedger" as const, id: "ELIGIBLE_EVENTS" }],
    }),

    // ── Member: my submissions ───────────────────────────────────────────────
    getMyCpdSubmissions: b.query<any[], void>({
      query: () => "/my/cpd/submissions",
      transformResponse: (r) => arr(r),
      providesTags: [{ type: "CpdLedger" as const, id: "SUBMISSIONS" }],
    }),

    submitExternalCpd: b.mutation<any, FormData>({
      query: (body) => ({ url: "/my/cpd/submissions", method: "POST", body }),
      invalidatesTags: [
        { type: "CpdLedger" as const, id: "SUBMISSIONS" },
        { type: "CpdLedger" as const, id: "ELIGIBLE_EVENTS" },
      ],
    }),

    // Manual/external CPD upload (no event) — self-declared points, pending review.
    // FormData: title, organizer?, activity_date, cpd_points_claimed, certificate, notes?
    submitManualCpd: b.mutation<any, FormData>({
      query: (body) => ({ url: "/my/cpd/submissions/manual", method: "POST", body }),
      invalidatesTags: [{ type: "CpdLedger" as const, id: "SUBMISSIONS" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMyCpdLedgerQuery,
  useAdminGetCpdEntriesQuery,
  useAdminGetCpdSummaryQuery,
  useAdminGetMemberCpdQuery,
  useAdminCreateCpdEntryMutation,
  useAdminGetCpdSubmissionsQuery,
  useAdminApproveCpdSubmissionMutation,
  useAdminRejectCpdSubmissionMutation,
  useGetCpdEligibleEventsQuery,
  useGetMyCpdSubmissionsQuery,
  useSubmitExternalCpdMutation,
  useSubmitManualCpdMutation,
} = cpdApi;
